// useAppSettings.js
// Tenant-wide on/off options from the Settings page (GET /app-settings). Until the answer
// arrives, or if it fails (server without V090), the defaults below apply.
import { useCallback, useEffect, useState } from "react";

export const APP_SETTING_DEFAULTS = {
  itemMasterPriceDecimals: true,
  itemPriceUpdatePriceDecimals: true,
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("jwtToken")}`,
});

export default function useAppSettings() {
  const tenancyId = localStorage.getItem("tenancyId");
  const [settings, setSettings] = useState(APP_SETTING_DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`/api/${tenancyId}/app-settings`, { headers: authHeaders() });
      if (res.ok) setSettings({ ...APP_SETTING_DEFAULTS, ...(await res.json()) });
    } catch {
      // keep defaults
    } finally {
      setLoaded(true);
    }
  }, [tenancyId]);

  useEffect(() => {
    load();
  }, [load]);

  /** Saves the given settings; resolves to the saved settings or throws with the server's message. */
  const save = useCallback(
    async (changes) => {
      const res = await fetch(`/api/${tenancyId}/app-settings`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(changes),
      });
      if (!res.ok) {
        let msg = `${res.status} ${res.statusText}`;
        try {
          const data = await res.json();
          msg = data?.errorMessage || data?.message || msg;
        } catch {
          // keep status text
        }
        throw new Error(msg);
      }
      const saved = { ...APP_SETTING_DEFAULTS, ...(await res.json()) };
      setSettings(saved);
      return saved;
    },
    [tenancyId]
  );

  return { settings, loaded, save, reload: load };
}

/** True when the value has a fractional part, e.g. "42.5". */
export const hasDecimals = (v) => {
  const n = Number(v);
  return v !== "" && v !== null && v !== undefined && Number.isFinite(n) && n % 1 !== 0;
};
